import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { formatDate } from 'lib/helpers/dateUtils';
import { filestackPath, ia, io, mString } from 'lib/helpers/utility';

import { SkeletonCellRenderer } from '../SkeletonCellRenderer';

export const isFirstColumn = (params) => {
  const displayedColumns = params.api.getAllDisplayedColumns();
  const thisIsFirstColumn = displayedColumns[0] === params.column;
  return thisIsFirstColumn;
};

export const defaultColDef = {
  minWidth: 100,
  headerCheckboxSelection: isFirstColumn,
  checkboxSelection: isFirstColumn
};

export const rowClassRules = {
  'ag-row-error': (params) => ia(params?.data?.error)
};

export const rowClassRulesGroupSelected = {
  'selected-group-row': (params) => params?.data?.isSelected
};

export const exportTableToCSV = (gridApi, categoryName) => {
  if (!gridApi) return;

  gridApi.exportDataAsCsv({
    fileName: `${categoryName || 'export'}.csv`,
    onlySelected: false,
    columnSeparator: ','
  });
};

const sumByColumn = (data) => {
  if (!ia(data)) return [];

  return data[0].map((_, colIndex) => {
    const allNonNumbers = data.every((row) => typeof row[colIndex] !== 'number');
    if (allNonNumbers) return '';

    return mString(
      data.reduce((sum, row) => {
        const value = typeof row[colIndex] === 'number' ? row[colIndex] : 0;
        return sum + value;
      }, 0)
    );
  });
};

// NOT USING ANYMORE
export const printTable = ({
  gridApi,
  categoryName,
  withTotals = true,
  practice,
  timezone = null
}) => {
  if (!gridApi) return;

  const columns = gridApi
    .getColumnDefs()
    .filter((col) => col.field !== 'actions' && !col.hide)
    .map((col) => {
      return { field: col.field, headerName: col.headerName };
    });

  const rows = [];
  const totals = [];
  gridApi.forEachNode((node) => {
    rows.push(
      columns.map((col) => {
        const column = gridApi.getColumnDef(col.field);
        if (column?.valueFormatter) {
          return column.valueFormatter({
            value: col.field.includes('.')
              ? col.field.split('.').reduce((obj, key) => obj?.[key], node.data)
              : node.data[col.field],
            data: node.data
          });
        }
        if (column?.valueGetter) {
          return column.valueGetter({
            value: col.field.includes('.')
              ? col.field.split('.').reduce((obj, key) => obj?.[key], node.data)
              : node.data[col.field],
            data: node.data
          });
        }
        return col.field.includes('.')
          ? col.field.split('.').reduce((obj, key) => obj?.[key], node.data)
          : node.data[col.field];
      })
    );
    if (withTotals) {
      totals.push(
        columns.map((col) => {
          const column = gridApi.getColumnDef(col.field);
          const value = col.field.includes('.')
            ? col.field.split('.').reduce((obj, key) => obj?.[key], node.data)
            : node.data[col.field];

          if (!isNaN(Number(value)) && (column?.valueFormatter || column?.valueGetter)) {
            return value;
          }

          return '';
        })
      );
    }
  });

  const columnHeaders = columns.map((col) => col.headerName);

  const totalsSum = withTotals ? sumByColumn(totals) : null;

  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.height;
  if (practice?.header_photo) {
    const headerPhotoData = JSON.parse(practice.header_photo);
    doc.addImage(filestackPath(headerPhotoData.jpg), 'JPEG', 14, 10, 50, 30);
  } else {
    doc.text(`${categoryName || 'Printed'} Table Report`, 14, 10);
  }
  autoTable(doc, {
    head: [columnHeaders],
    body: rows,
    foot: withTotals ? [totalsSum] : undefined,
    startY: 20,
    headStyles: { fillColor: '#004F6B' },
    footStyles: { fillColor: '#004F6B' },
    showFoot: withTotals ? 'lastPage' : false,
    didDrawPage: () => {
      const printDate = formatDate(new Date(), timezone, 'MM/dd/yyyy h:mm:ss a');
      const pageWidth = doc.internal.pageSize.width;
      doc.setFontSize(8);

      const dateText = `Print date: ${printDate}`;
      const dateTextWidth = doc.getStringUnitWidth(dateText) * 3;
      doc.text(dateText, pageWidth - dateTextWidth - 10, pageHeight - 10);
    }
  });

  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageWidth = doc.internal.pageSize.width;
    doc.setFontSize(8);
    const pageText = `${i} of ${totalPages}`;
    const pageTextWidth = doc.getStringUnitWidth(pageText) * 3;
    doc.text(pageText, (pageWidth - pageTextWidth) / 2, pageHeight - 10);
  }

  const pdfBlob = doc.output('blob');
  const pdfURL = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfURL);
  if (printWindow) {
    printWindow.onload = () => printWindow.print();
  }
};

export const getActionColumnDef = (field) => {
  if (field !== 'actions') return {};

  return {
    suppressMovable: true,
    lockVisible: true,
    suppressToolPanel: true,
    minWidth: 88
  };
};

export const getGroupedColumnField = (api, columnId) => {
  if (columnId !== 'ag-Grid-AutoColumn' || !api) return null;

  const columnState = api.getColumnState();
  const groupedColumn = columnState.find(
    (col) => col.rowGroup === true && (col.rowGroupIndex === 0 || col.rowGroupIndex === null)
  );

  if (groupedColumn) {
    const columnDefs = api.getColumnDefs();
    const columnDef = columnDefs.find((def) => {
      const defColId = def.colId || def.field;
      return defColId === groupedColumn.colId;
    });
    return {
      colId: groupedColumn.colId,
      field: columnDef?.field || groupedColumn.colId
    };
  }

  return null;
};

export const makeSkeletonRows = (length) =>
  Array.from({ length }, (_, index) => ({ index, _isSkeleton: true }));

export const skeletonGetRowId = (getRowId) => (params) =>
  params.data?._isSkeleton ? `skeleton-${params.data.index}` : String(getRowId(params));

export const applySkeletonRenderers = (colDefs) =>
  colDefs?.map((col) => ({
    ...col,
    ...(ia(col.children) ? { children: applySkeletonRenderers(col.children) } : {}),
    cellRendererSelector: (params) => {
      if (params.data?._isSkeleton) return { component: SkeletonCellRenderer };
      return col?.cellRendererSelector?.(params);
    }
  }));

export const checkIsValidClick = (isLoading, event, handler) => {
  if (isLoading || typeof handler !== 'function') return false;

  const data = event?.data;

  if (!io(data)) return false;

  if (data?._isSkeleton) return false;

  if (Object.keys(data).length === 1 && data?.index !== undefined) {
    return false;
  }

  return true;
};
